<template>
  <Layout>
    <Head title="Appointments - Kiosk" />

    <div class="p-4 sm:p-6 md:p-8">
      <div class="space-y-6">
        <div class="border-b pb-4">
          <h1 class="text-2xl font-bold text-gray-900">{{ t('nav.appointments') }}</h1>
          <p class="text-gray-600 mt-1">{{ t('appointments.view_manage') }}</p>
        </div>

        <!-- Upcoming Appointments -->
        <Card v-if="upcomingAppointments.length > 0">
          <CardHeader>
            <CardTitle>{{ t('appointments.upcoming') }}</CardTitle>
            <CardDescription>
              {{ t('appointments.appointment_count', { count: upcomingAppointments.length }) }}
            </CardDescription>
          </CardHeader>

          <CardContent>
            <div class="space-y-4">
              <div
                v-for="appointment in upcomingAppointments"
                :key="appointment.id"
                class="border rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div class="space-y-4">
                  <div class="flex items-start justify-between">
                    <div class="space-y-2">
                      <div class="flex items-center gap-2">
                        <h3 class="font-medium text-gray-900">
                          {{ appointment.appointment_type_name || appointment.legal_case_type_name }}
                        </h3>
                        <!-- Canceled badge -->
                        <span v-if="appointment.scheduling_status === 'C'" class="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full">
                          <svg class="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                          </svg>
                          Canceled
                        </span>
                        <!-- Rescheduled badge -->
                        <span v-if="appointment.scheduling_status === 'R'" class="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-700 rounded-full">
                          <svg class="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                          </svg>
                          Rescheduled
                        </span>
                      </div>
                      <p class="text-sm text-gray-600">
                        {{ appointment.legal_case_type_description }}
                      </p>
                    </div>
                  </div>

                  <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                    <div class="flex items-center gap-2 text-gray-600">
                      <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span>{{ t('appointments.appointment_taken_at') }} {{ formatDate(appointment.booking_date) }}</span>
                    </div>
                    <div class="flex items-center gap-2 text-gray-600">
                      <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
                      </svg>
                      <span>{{ t('appointments.scheduled_for_detail') }} {{ formatDateTime(appointment.slot_start_datetime) }}</span>
                    </div>
                    <div v-if="appointment.slot_end_datetime" class="flex items-center gap-2 text-gray-600">
                      <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>{{ t('appointments.duration') }} {{ formatTimeRange(appointment.slot_start_datetime, appointment.slot_end_datetime) }}</span>
                    </div>
                  </div>

                  <div v-if="appointment.location || appointment.primary_attorney_name" class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm border-t pt-3">
                    <div v-if="appointment.location" class="flex items-start gap-2 text-gray-600">
                      <svg class="h-4 w-4 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <div>
                        <span class="font-medium">{{ t('common.location') }}</span>
                        <span class="ml-1">{{ appointment.location }}</span>
                      </div>
                    </div>

                    <div v-if="appointment.primary_attorney_name" class="flex items-start gap-2 text-gray-600">
                      <svg class="h-4 w-4 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <div>
                        <span class="font-medium">{{ t('common.attorney') }}</span>
                        <span class="ml-1">{{ appointment.primary_attorney_name }}</span>
                        <span v-if="appointment.primary_attorney_email" class="block text-xs text-gray-500">
                          {{ appointment.primary_attorney_email }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <!-- Past Appointments -->
        <Card v-if="pastAppointments.length > 0">
          <CardHeader>
            <CardTitle>{{ t('appointments.past') }}</CardTitle>
            <CardDescription>
              {{ t('appointments.appointment_count', { count: pastAppointments.length }) }}
            </CardDescription>
          </CardHeader>

          <CardContent>
            <div class="space-y-4">
              <div
                v-for="appointment in pastAppointments"
                :key="appointment.id"
                class="border border-gray-200 rounded-lg p-4 bg-gray-50 opacity-75"
              >
                <div class="space-y-4">
                  <div class="flex items-start justify-between">
                    <div class="space-y-2">
                      <div class="flex items-center gap-2 flex-wrap">
                        <h3 class="font-medium text-gray-700">
                          {{ appointment.appointment_type_name || appointment.legal_case_type_name }}
                        </h3>
                        <!-- Canceled badge -->
                        <span v-if="appointment.scheduling_status === 'C'" class="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full">
                          <svg class="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                          </svg>
                          Canceled
                        </span>
                        <!-- Rescheduled badge -->
                        <span v-if="appointment.scheduling_status === 'R'" class="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-700 rounded-full">
                          <svg class="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                          </svg>
                          Rescheduled
                        </span>
                        <!-- Completed badge -->
                        <span class="inline-flex items-center gap-1 px-2 py-1 text-xs bg-gray-200 text-gray-600 rounded-full">
                          <svg class="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm.707-10.293a1 1 0 00-1.414-1.414L9 6.586 7.707 5.293a1 1 0 00-1.414 1.414L8.586 9l-2.293 2.293a1 1 0 101.414 1.414L9 10.414l2.293 2.293a1 1 0 001.414-1.414L10.414 9l2.293-2.293z" clip-rule="evenodd" />
                          </svg>
                          {{ t('appointments.appointment_completed') }}
                        </span>
                      </div>
                      <p class="text-sm text-gray-500">
                        {{ appointment.legal_case_type_description }}
                      </p>
                    </div>
                  </div>

                  <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                    <div class="flex items-center gap-2 text-gray-500">
                      <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span>{{ t('appointments.appointment_taken_at') }} {{ formatDate(appointment.booking_date) }}</span>
                    </div>
                    <div class="flex items-center gap-2 text-gray-500">
                      <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
                      </svg>
                      <span>{{ t('appointments.scheduled_for_detail') }} {{ formatDateTime(appointment.slot_start_datetime) }}</span>
                    </div>
                    <div v-if="appointment.slot_end_datetime" class="flex items-center gap-2 text-gray-500">
                      <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>{{ t('appointments.duration') }} {{ formatTimeRange(appointment.slot_start_datetime, appointment.slot_end_datetime) }}</span>
                    </div>
                  </div>

                  <div v-if="appointment.location || appointment.primary_attorney_name" class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm border-t border-gray-300 pt-3">
                    <div v-if="appointment.location" class="flex items-start gap-2 text-gray-500">
                      <svg class="h-4 w-4 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <div>
                        <span class="font-medium">{{ t('common.location') }}</span>
                        <span class="ml-1">{{ appointment.location }}</span>
                      </div>
                    </div>

                    <div v-if="appointment.primary_attorney_name" class="flex items-start gap-2 text-gray-500">
                      <svg class="h-4 w-4 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <div>
                        <span class="font-medium">{{ t('common.attorney') }}</span>
                        <span class="ml-1">{{ appointment.primary_attorney_name }}</span>
                        <span v-if="appointment.primary_attorney_email" class="block text-xs text-gray-400">
                          {{ appointment.primary_attorney_email }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <!-- No appointments message -->
        <Card v-if="upcomingAppointments.length === 0 && pastAppointments.length === 0">
          <CardContent>
            <div class="text-center py-12">
              <div class="mx-auto h-24 w-24 text-gray-400">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 class="mt-4 text-sm font-medium text-gray-900">{{ t('appointments.no_appointments') }}</h3>
              <p class="mt-2 text-sm text-gray-500">
                {{ t('appointments.no_scheduled_message') }}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  </Layout>
</template>

<script setup>
import { Head } from '@inertiajs/vue3'
import { computed } from 'vue'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import Layout from '@/Layouts/Minimal.vue'
import { t } from '@/entrypoints/translations.js'

// Props
const props = defineProps({
  appointments: [Array, Object],
})

// Computed properties to separate upcoming and past appointments
const upcomingAppointments = computed(() => {
  if (!props.appointments || !Array.isArray(props.appointments)) return []
  const now = new Date()
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  
  return props.appointments.filter(appointment => {
    if (!appointment.slot_start_datetime) return true
    const appointmentStart = new Date(appointment.slot_start_datetime)
    // Include appointments that start today or in the future
    return appointmentStart >= startOfToday
  })
})

const pastAppointments = computed(() => {
  if (!props.appointments || !Array.isArray(props.appointments)) return []
  const now = new Date()
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  
  return props.appointments.filter(appointment => {
    if (!appointment.slot_start_datetime) return false
    const appointmentStart = new Date(appointment.slot_start_datetime)
    // Only include appointments that started before today
    return appointmentStart < startOfToday
  })
})

// Helper function to format dates
const formatDate = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

// Helper function to format date and time
const formatDateTime = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short'
  })
}

// Helper function to format time range duration
const formatTimeRange = (startDateString, endDateString) => {
  const startDate = new Date(startDateString)
  const endDate = new Date(endDateString)
  
  const startTime = startDate.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short'
  })
  
  const endTime = endDate.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short'
  })
  
  return `${startTime} - ${endTime}`
}
</script>
